import {
  Badge,
  CheckableAvatarSelectItem,
  DropdownSelect,
  Icon,
  Tooltip,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';

type ConversationTimelineDetailsRequesterFilterProps = {
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  onClear: () => void;
};

export const ConversationTimelineDetailsRequesterFilter = ({
  selectedIds,
  onChange,
  onClear,
}: ConversationTimelineDetailsRequesterFilterProps): JSX.Element => {
  const initialArg: ReadSearchApiArg<'users'> = useMemo(
    () => ({
      scope: 'users',
      sortBy: { field: 'display_name', order: 'asc' },
    }),
    [],
  );

  const [readSearchUsers, { results: users, hasNextPage, fetchNextPage }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  const [searchTerm, setSearchTerm] = useState<string>();

  useUpdateEffect(() => {
    readSearchUsers(
      {
        ...initialArg,
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchUsers, initialArg, searchTerm]);

  const dropdownItems = useMemo(
    () =>
      users.map(({ id, name, email, phone_number }) => ({
        id,
        name: name || email || phone_number || id,
      })),
    [users],
  );

  const handleSearch = useCallback((value?: string) => {
    setSearchTerm(value?.trim() || undefined);
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !users.length) {
        readSearchUsers({
          ...initialArg,
          query: searchTerm,
        });
      } else {
        setSearchTerm(undefined);
      }
    },
    [readSearchUsers, initialArg, searchTerm, users.length],
  );

  const handleChange = useCallback(
    (item, checked: boolean) => {
      if (checked) {
        onChange([...selectedIds, item.id]);
      } else {
        onChange(selectedIds.filter((ids) => ids !== item.id));
      }
    },
    [selectedIds, onChange],
  );

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow
      items={dropdownItems}
      selectedItems={selectedIds}
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{
        variant: 'tertiary',
      }}
      disabledItems={selectedIds.length === 1 ? selectedIds : undefined}
      tooltipItems={
        selectedIds.length === 1
          ? {
              [selectedIds[0]]: {
                content: 'At least 1 requester should be selected',
              },
            }
          : {}
      }
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(fetchNextPage()),
      }}
      onClearText="Reset"
      onChange={handleChange}
      onClear={onClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      <Tooltip
        content="Filter by requester"
        wrap="div"
        wrapProps={{
          style: { display: 'flex', gap: 'var(--dixa-size-spacer-100)' },
        }}
      >
        <Icon name="filter" />
        <Badge variant="secondary" size="small">
          {selectedIds.length}
        </Badge>
      </Tooltip>
    </DropdownSelect>
  );
};
